import type { V2Inspection } from '../domain/inspection';
import { agreementSignedForInspection, formalWorkflowBlocked, formalWorkflowGate, type FormalWorkflowGate } from '../domain/inspectionContext';
import { readInspectorProfile } from './preferences';

// THE canonical answer to "may this inspection continue the formal New York workflow right now?",
// for every store action and every screen (NY F1 Round 1; OC-1 — Round 5 adds the X-1 agreement
// state).
//
// The pure derivation lives in src/domain/inspectionContext.ts. This module is the one place that
// pairs it with the device's inspector profile, so no store and no screen re-states the rule —
// there is exactly one gate, and `AreaWorkspaceScreen`, Property Layout, the final report, the
// finalize transition and the Agreement all get the SAME answer for the same persisted facts.
// (`aiStore.aiPolicyBlockFor` is the same pairing for the AI-specific PP-21 question, which adds
// the client's consent decision on top.)
//
// TWO gates, deliberately separate and ordered:
//   ELIGIBILITY  does the supported New York workflow apply to this inspection at all — licence,
//                property state, the occupancy answer that picks the service, the statutory unit
//                limit? This is what the CREATION form and the AGREEMENT itself ask, because an
//                inspection may be booked before it is signed and the signing path must not be
//                refused for the very state it exists to leave.
//   FIELD WORK   eligibility PLUS X-1 (Owner default 2026-09-11): the pre-inspection agreement for
//                this service is signed. Areas, checklist rows, completion and the report wait for
//                it; creating and viewing never do.
//
// What this gate is NOT: it is not OC-2's Report Readiness block. Whether a report has everything it
// needs is Round 2 and is decided by src/state/reportReadiness.ts.
export function formalEligibilityGateFor(inspection: V2Inspection): FormalWorkflowGate {
  return formalWorkflowGate({ inspection, inspectorState: readInspectorProfile().licensedState });
}

export function formalWorkflowGateFor(inspection: V2Inspection): FormalWorkflowGate {
  return formalWorkflowGate({ inspection, inspectorState: readInspectorProfile().licensedState, agreementSigned: agreementSignedForInspection(inspection) });
}

// Why the formal workflow may not continue, or null when it may. The values match the AiPolicyBlock
// vocabulary so one inspection is described the same way wherever it is refused.
export type FormalWorkflowRefusal = 'blockedLegalReview' | 'blockedJurisdiction' | 'blockedAgreementUnsigned';

function refusalOf(gate: FormalWorkflowGate): FormalWorkflowRefusal | null {
  if (!formalWorkflowBlocked(gate)) return null;
  if (gate.kind === 'legalReviewBlock') return 'blockedLegalReview';
  if (gate.kind === 'agreementUnsigned') return 'blockedAgreementUnsigned';
  return 'blockedJurisdiction';
}

export function formalWorkflowRefusalFor(inspection: V2Inspection): FormalWorkflowRefusal | null {
  return refusalOf(formalWorkflowGateFor(inspection));
}

export function formalEligibilityRefusalFor(inspection: V2Inspection): FormalWorkflowRefusal | null {
  return refusalOf(formalEligibilityGateFor(inspection));
}

export function formalWorkflowBlockedFor(inspection: V2Inspection): boolean {
  return formalWorkflowRefusalFor(inspection) !== null;
}

// Eligibility only: what the Agreement card asks before offering to sign or print, and what decides
// whether a conditional checklist row may be seeded onto a booked-but-unsigned inspection.
export function formalEligibilityBlockedFor(inspection: V2Inspection): boolean {
  return formalEligibilityRefusalFor(inspection) !== null;
}

// Thrown by the mutating actions whose signature has no room for a refusal — exactly as
// InspectionLockedError is. A caller that reaches one of these programmatically, without passing a
// screen, is refused loudly rather than silently writing nothing.
export class FormalWorkflowBlockedError extends Error {
  readonly refusal: FormalWorkflowRefusal;

  constructor(refusal: FormalWorkflowRefusal) {
    super(`the formal New York workflow is blocked for this inspection: ${refusal}`);
    this.name = 'FormalWorkflowBlockedError';
    this.refusal = refusal;
  }
}

// Fail-closed, like the AI chokepoint: an inspection that cannot even be resolved is not eligible.
export function assertFormalWorkflowEligible(inspection: V2Inspection | null | undefined): void {
  if (!inspection) throw new FormalWorkflowBlockedError('blockedJurisdiction');
  const refusal = formalWorkflowRefusalFor(inspection);
  if (refusal) throw new FormalWorkflowBlockedError(refusal);
}

// The signing path asks ELIGIBILITY only — otherwise the unsigned state would refuse the signature
// that leaves it (X-1).
export function assertAgreementSignable(inspection: V2Inspection | null | undefined): void {
  if (!inspection) throw new FormalWorkflowBlockedError('blockedJurisdiction');
  const refusal = formalEligibilityRefusalFor(inspection);
  if (refusal) throw new FormalWorkflowBlockedError(refusal);
}
